/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableModel} from '../../shared/table/models/table.model';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {BundleSettlementsColumnEnum} from './models/bundle-settlements-column.enum';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {SettlementDetailModel} from '../models/settlement/settlement-detail/settlement-detail.model';
import bundleSettlementsTableHeadersConfiguration from './configuration/bundle-settlements-table-headers-configuration';
import {BundleTypeEnum} from '../bundles-main-page/data/bundle-type.enum';
import {CurrencyBaseModel} from '../models/currency/currency-base.model';
import {Subscription} from 'rxjs';
import {BundlesApi} from '../api/bundles.api';
import {
    SettlementDetailLineIdentifierModel
} from '../models/settlement/settlement-detail/settlement-detail-line-identifier.model';
import {SelectedTabsService} from '../services/selected-tabs.service';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {BundleSettlementsTableState} from './models/bundle-settlements-table.state.model';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {PeriodViewTabsEnum} from '../bundles-main-page/data/period-view-tabs.enum';

@Component({
    selector: 'app-bundle-settlements-table',
    templateUrl: './bundle-settlements-table.component.html',
    styleUrls: ['./bundle-settlements-table.component.css', '../styles/bundles-base.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BundleSettlementsTableComponent extends StatefulComponent<BundleSettlementsTableState>
        implements OnInit, OnDestroy, OnChanges {
    @Input() settlementsData: SettlementDetailModel[];
    @Input() currency: CurrencyBaseModel;
    // Full line tooltip detail
    private readonly FULL_LINE_TOOLTIP_DETAIL: string = 'settlement line';
    // The table data to be injected in the table component
    public tableData: TableModel<BundleSettlementsColumnEnum, SettlementDetailModel, SettlementDetailLineIdentifierModel> = {
        headers: {
            entries: new Map<BundleSettlementsColumnEnum, TableHeaderEntryModel<BundleSettlementsColumnEnum>>([
                [BundleSettlementsColumnEnum.SUPPLIER, bundleSettlementsTableHeadersConfiguration.suppliers],
                [BundleSettlementsColumnEnum.COMPLAINT_CODE, bundleSettlementsTableHeadersConfiguration.complaint],
                [BundleSettlementsColumnEnum.SETTLEMENT_CODE, bundleSettlementsTableHeadersConfiguration.settlement],
                [BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST, bundleSettlementsTableHeadersConfiguration.partsTotalNetAmount],
                [BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST, bundleSettlementsTableHeadersConfiguration.partsTotalGrossAmount],
                [BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST, bundleSettlementsTableHeadersConfiguration.teMinutesTotalAmount]
            ])
        },
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true
    };
    private subscriptions = new Subscription();

    /**
     * Returns if the te minutes total amount column should be visible
     */
    get hasTeMinutesTotalAmountColumn(): boolean {
        return this.selectedTabsService.bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING;
    }

    /**
     * Returns if the parts amount columns should be visible
     */
    get hasPartsAmountsColumns(): boolean {
        return this.selectedTabsService.bundleType === BundleTypeEnum.PARTS_BUNDLING;
    }

    /**
     * Return if the current view is the Settlements View
     */
    get isCurrentViewSettlementsView(): boolean {
        return this.periodsViewsService.getCurrentView() === PeriodViewTabsEnum.SETTLEMENTS;
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): SettlementDetailModel | null {
        let accordionData: SettlementDetailModel | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    constructor(private bundlesApi: BundlesApi,
                private selectedTabsService: SelectedTabsService,
                private periodsViewsService: PeriodsViewsService) {
        super();
    }

    ngOnInit(): void {
        this.tableData.staticSection = this.mapData(this.settlementsData);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Here we need to listen for @Input settlementsData changes to update the tableData staticSection results otherwise they would not
        // be updated because the mapData is only being done when the component is initialized.
        if (changes?.['settlementsData']) {
            this.tableData.staticSection = this.mapData(this.settlementsData);
            this.tableData.noFilterResultsFoundTemplate = this.periodsViewsService.isGlobalSearchCriteriaApplied();
        }
    }

    loadDetailData(line: TableLineModel<BundleSettlementsColumnEnum,
            SettlementDetailModel, SettlementDetailLineIdentifierModel>): void {
        if (line != null && line?.accordionData == null) {
            const complaintCode: string = line.identifier?.complaintCode;
            const settlementCode: string = line.identifier?.settlementCode;
            line.accordionData = this.settlementsData.find((settlement) =>
                    settlementCode === settlement.settlementCode && complaintCode === settlement.complaintCode);
        }
        this.storeState({selectedLine: line});
    }

    private mapData(settlements: SettlementDetailModel[]):
            TableLineModel<BundleSettlementsColumnEnum, SettlementDetailModel, SettlementDetailLineIdentifierModel>[] {
        this.updateDynamicColumnsHeaders();

        let lines: TableLineModel<BundleSettlementsColumnEnum, SettlementDetailModel, SettlementDetailLineIdentifierModel>[] = [];
        if (settlements != null) {
            lines = settlements.map((settlement) => ({
                isOpen: false,
                identifier: {
                    complaintCode: settlement?.complaintCode,
                    settlementCode: settlement?.settlementCode
                },
                data: this.mapLineData(settlement)
            } as TableLineModel<BundleSettlementsColumnEnum, SettlementDetailModel, SettlementDetailLineIdentifierModel>));
        }

        return lines;
    }

    private mapLineData(data: SettlementDetailModel): Map<BundleSettlementsColumnEnum, TableLineDataModel> {
        const map: Map<BundleSettlementsColumnEnum, TableLineDataModel> = new Map<BundleSettlementsColumnEnum, TableLineDataModel>([
            [
                BundleSettlementsColumnEnum.SUPPLIER, {
                cellValue: data.supplier
            }
            ],
            [
                BundleSettlementsColumnEnum.COMPLAINT_CODE, {
                cellValue: data.complaintCode
            }
            ],
            [
                BundleSettlementsColumnEnum.SETTLEMENT_CODE, {
                cellValue: data.settlementCode
            }
            ],
            [
                BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST, {
                cellValue: {
                    data: data.materialsTotalNetCost,
                    currency: this.currency
                },
                copyPasteValue: data.materialsTotalNetCost
            }
            ],
            [
                BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST, {
                cellValue: {
                    data: data.materialsTotalGrossCost,
                    currency: this.currency
                },
                copyPasteValue: data.materialsTotalGrossCost
            }
            ],
            [
                BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST, {
                cellValue: {
                    data: data.teMinutesTotalCost,
                    currency: this.currency
                }
            }
            ]
        ]);
        this.removeHiddenColumnsValues(map);
        return map;
    }

    /**
     * Adds or removes the dynamic columns headers (TE_MINUTES_TOTAL_COST, PARTS_TOTAL_GROSS_COST and PARTS_TOTAL_NET_COST )
     */
    private updateDynamicColumnsHeaders(): void {
        if (!this.hasTeMinutesTotalAmountColumn) {
            this.tableData.headers.entries.delete(BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST);
        } else if (!this.tableData.headers.entries.has(BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST)) {
            this.tableData.headers.entries.set(BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST,
                    bundleSettlementsTableHeadersConfiguration.teMinutesTotalAmount);
        }

        if (!this.hasPartsAmountsColumns) {
            this.tableData.headers.entries.delete(BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST);
            this.tableData.headers.entries.delete(BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST);
        } else {
            if (!this.tableData.headers.entries.has(BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST)) {
                this.tableData.headers.entries.set(BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST,
                        bundleSettlementsTableHeadersConfiguration.partsTotalGrossAmount);
            }

            if (!this.tableData.headers.entries.has(BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST)) {
                this.tableData.headers.entries.set(BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST,
                        bundleSettlementsTableHeadersConfiguration.partsTotalNetAmount);
            }
        }
    }

    /**
     * Removes the dynamic columns values from the value map (TE_MINUTES_TOTAL_COST, PARTS_TOTAL_GROSS_COST and PARTS_TOTAL_NET_COST )
     */
    private removeHiddenColumnsValues(map: Map<BundleSettlementsColumnEnum, TableLineDataModel>): void {
        if (!this.hasTeMinutesTotalAmountColumn) {
            map.delete(BundleSettlementsColumnEnum.TE_MINUTES_TOTAL_COST);
        }

        if (!this.hasPartsAmountsColumns) {
            map.delete(BundleSettlementsColumnEnum.PARTS_TOTAL_GROSS_COST);
            map.delete(BundleSettlementsColumnEnum.PARTS_TOTAL_NET_COST);
        }
    }
}
